import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { getReviews, getReviewsSummary } from "../lib/api";
import { shortPlace } from "../lib/routeTitle";
import { RATINGS_COPY } from "../i18n/homeContentI18n";

const T_DE = { eyebrow: "Echte Bewertungen", title: "Was unsere Fahrgäste sagen", sub: "Bewertungen von Kundinnen und Kunden nach ihrer Fahrt.", based: "aus {n} Bewertungen", google: "Auf Google bewerten" };

export const CustomerRatings = () => {
  const { lang } = useLang();
  const t = lang === "de" ? T_DE : (RATINGS_COPY[lang] || RATINGS_COPY.en);
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    getReviews().then((r) => setReviews(Array.isArray(r) ? r : [])).catch(() => setReviews([]));
    getReviewsSummary().then((s) => setSummary(s)).catch(() => setSummary(null));
  }, []);

  if (reviews.length < 1) return null; // show as soon as we have at least one real rating

  return (
    <section className="py-16 md:py-24 bg-white" data-testid="customer-ratings-section">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#B98A00] bg-[#F5B400]/10 rounded-full px-3 py-1">
              <Star className="w-3.5 h-3.5 fill-[#F5B400] text-[#F5B400]" /> {t.eyebrow}
            </div>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-bold text-neutral-900">{t.title}</h2>
            <p className="mt-3 text-neutral-500 text-base">{t.sub}</p>
          </div>
          {summary && summary.count > 0 && (
            <div className="flex items-center gap-4 rounded-2xl border border-neutral-200 bg-neutral-50 shadow-sm px-6 py-4 shrink-0" data-testid="ratings-aggregate">
              <div className="text-4xl font-display font-bold text-neutral-900" data-testid="ratings-average">{Number(summary.average).toFixed(1)}</div>
              <div>
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className={`w-4 h-4 ${n <= Math.round(summary.average) ? "fill-[#F5B400] text-[#F5B400]" : "text-neutral-300"}`} />
                  ))}
                </div>
                <p className="text-xs text-neutral-500 mt-1" data-testid="ratings-count">{t.based.replace("{n}", summary.count)}</p>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6">
          <a href="https://www.google.com/maps?cid=8059183557833479690" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-neutral-300 bg-white hover:border-[#F5B400] hover:bg-[#F5B400]/5 px-5 py-2.5 text-sm font-semibold text-neutral-800 transition-colors" data-testid="google-review-cta">
            <Star className="w-4 h-4 fill-[#F5B400] text-[#F5B400]" /> {t.google}
          </a>
        </div>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {reviews.slice(0, 6).map((r, i) => (
            <motion.div key={i}
              initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
              className="relative rounded-2xl border border-neutral-200 bg-neutral-50 p-6 hover:shadow-md transition-shadow"
              data-testid={`review-card-${i}`}>
              <Quote className="w-7 h-7 text-[#F5B400]/40 absolute top-5 right-5" />
              <div className="flex items-center gap-0.5" data-testid={`review-stars-${i}`}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star key={n} className={`w-4 h-4 ${n <= r.stars ? "fill-[#F5B400] text-[#F5B400]" : "text-neutral-300"}`} />
                ))}
              </div>
              {r.comment ? <p className="mt-3 text-[15px] leading-relaxed text-neutral-700">“{r.comment}”</p> : null}
              <div className="mt-4 flex items-center gap-3">
                <span className="w-9 h-9 rounded-full bg-neutral-900 text-white flex items-center justify-center text-sm font-bold shrink-0">
                  {(r.name || "?").charAt(0).toUpperCase()}
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-neutral-900 truncate">{r.name}</div>
                  {r.city ? <div className="text-xs text-neutral-500 truncate">Flughafen Zürich → {shortPlace(r.city)}</div> : null}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CustomerRatings;
